import type { DeadlineVerdict } from '../dispatch.api';

export type DeadlineTone = 'ok' | 'risk' | 'neutral';

export interface DeadlineText {
  tone: DeadlineTone;
  /** The verdict in words, so it never rests on colour alone. */
  headline: string;
  detail: string | null;
}

const SECONDS_PER_HOUR = 3600;
const HOURS_PER_DAY = 24;

/** "1 day 3 hours", "5 hours", "45 minutes": the size of a gap, coarse enough to read at a glance. */
export function spanText(totalSeconds: number): string {
  const seconds = Math.abs(totalSeconds);
  const days = Math.floor(seconds / (SECONDS_PER_HOUR * HOURS_PER_DAY));
  const hours = Math.floor((seconds % (SECONDS_PER_HOUR * HOURS_PER_DAY)) / SECONDS_PER_HOUR);
  if (days === 0 && hours === 0) {
    const minutes = Math.max(1, Math.floor(seconds / 60));
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }
  const parts = [days > 0 ? `${days} ${days === 1 ? 'day' : 'days'}` : null, hours > 0 ? `${hours} ${hours === 1 ? 'hour' : 'hours'}` : null];
  return parts.filter(Boolean).join(' ');
}

/** What the plan card says for one deadline: `deadlineName` is "delivery deadline" or "empty-return deadline". */
export function deadlineText(verdict: DeadlineVerdict, deadlineName: string): DeadlineText {
  switch (verdict.status) {
    case 'on_time':
      return { tone: 'ok', headline: 'On time', detail: `Arrives ${spanText(verdict.slackSeconds)} before the ${deadlineName} ends, rest included.` };
    case 'at_risk':
      return { tone: 'risk', headline: 'At risk', detail: `Arrives ${spanText(verdict.slackSeconds)} after the ${deadlineName} ends, rest included.` };
    case 'no_deadline':
      return { tone: 'neutral', headline: 'No deadline set', detail: `The job has no ${deadlineName}, so there is nothing to judge the plan against.` };
    case 'no_route':
      return { tone: 'neutral', headline: 'No verdict', detail: 'Route times are not available for this plan.' };
    case 'not_applicable':
      return { tone: 'neutral', headline: 'Not applicable', detail: null };
  }
}
